import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {P} from 'UI/components/layout/Text';

import {StarFilledIcon, StarIcon} from 'Images/icons';

/**
 * The props for the `StarRating` component.
 * Displays a read-only star rating with an optional review count.
 */
interface ComponentProps {
    /** The optional number of reviews, displayed as "(count Bewertungen)". */
    count?: number;
    /**
     * The rating value from 0 to 5.
     * Determines how many stars are filled (accentPale) vs empty (inkDisabled).
     */
    rating: number;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'StarRating'
     */
    testId?: string;
}

/**
 * A read-only star rating display matching the Figma design system.
 * Shows 5 star characters with filled/empty states and an optional review count.
 *
 * @param props        The component props.
 * @param props.count  Optional number of reviews.
 * @param props.rating The rating value (0–5).
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'StarRating'`.
 * @returns A JSX element rendering the StarRating.
 *
 * @example
 * ```tsx
 * <StarRating rating={4.8} count={128} />
 * <StarRating rating={3} />
 * ```
 */
const StarRating = ({count, rating, testId = 'StarRating'}: ComponentProps) => (
    <Wrapper data-cy={testId}>
        <StarsRow>
            {Array.from({length: 5}, (_, i) => (
                <Star key={i} $filled={i < Math.round(rating)}>
                    {i < Math.round(rating)
                        ? <StarFilledIcon height="1em" width="1em" />
                        : <StarIcon height="1em" width="1em" />}
                </Star>
            ))}
        </StarsRow>
        {/* ⚠️ Figma: count 12px Regular — using labelSmall (11px/500), 1px kleiner */}
        {typeof count === 'number' && (
            <CountLabel $size="labelSmall" as="span" data-cy={`${testId}-count`}>
                {rating.toFixed(1)} ({count} Bewertungen)
            </CountLabel>
        )}
    </Wrapper>
);

StarRating.displayName = 'StarRating';

export {StarRating};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
`;

const StarsRow = styled.div`
    display: flex;
    gap: ${spacing(1)};
`;

interface StarProps {
    /** Whether this star is filled. */
    $filled: boolean;
}

const Star = styled.span<StarProps>`
    align-items: center;
    color: ${({$filled, theme}) => ($filled ? theme.colors.accentPale : theme.colors.inkDisabled)};
    display: inline-flex;
    font-size: 2rem;
    line-height: 1;
`;

const CountLabel = styled(P)`
    color: ${({theme}) => theme.colors.inkTertiary};
`;